import { formatTimestamp, humanizeEnum } from '@/lib/format';
import type { DashboardResponse } from '@/types';

function escapeCsvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  if (text.includes(',') || text.includes('"') || text.includes('\n')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

type CsvRow = (string | number | null | undefined)[];

function rowsToCsv(rows: CsvRow[]): string {
  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

/** Trigger a browser download for an already-built blob (e.g. a server-generated CSV). */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadCsv(filename: string, rows: CsvRow[]): void {
  const content = rowsToCsv(rows);
  downloadBlob(filename, new Blob([content], { type: 'text/csv;charset=utf-8;' }));
}

export function buildDashboardCsvRows(data: DashboardResponse, days: number): CsvRow[] {
  const rows: CsvRow[] = [];
  const kpi = data.kpi;
  const tiles = data.actionTiles;
  const autopilot = data.autopilot;

  rows.push(['Dashboard export', `Last ${days} days`]);
  rows.push(['Exported at', formatTimestamp()]);
  rows.push([]);

  rows.push(['KPIs', 'Value']);
  rows.push(['Total returns', kpi?.totalReturns ?? '']);
  rows.push(['Previous total returns', kpi?.prevTotalReturns ?? '']);
  rows.push(['Return rate (%)', kpi?.returnRate ?? '']);
  rows.push(['Refunds issued', kpi?.refundsIssued ?? '']);
  rows.push(['Revenue saved', kpi?.revenueSaved ?? '']);
  rows.push(['Store credit adoption (%)', kpi?.storeCreditAdoption ?? '']);
  rows.push(['Exchange conversion (%)', kpi?.exchangeConversion ?? '']);
  rows.push(['Avg resolve days', kpi?.avgResolveDays ?? '']);
  rows.push(['Pending review', kpi?.pendingReview ?? '']);
  rows.push(['Pending QC', kpi?.pendingQc ?? '']);
  rows.push(['Active pickups', kpi?.activePickups ?? '']);
  rows.push(['At warehouse', kpi?.receivedAtWarehouse ?? '']);
  rows.push(['Exceptions', kpi?.exceptions ?? '']);
  rows.push(['Rejected', kpi?.rejected ?? '']);
  rows.push([]);

  rows.push(['Needs attention', 'Value']);
  rows.push(['Awaiting approval', tiles?.awaitingApproval ?? '']);
  rows.push(['QC queue', tiles?.qcQueue ?? '']);
  rows.push(['Exceptions to resolve', tiles?.exceptionsToResolve ?? '']);
  rows.push([]);

  rows.push(['Autopilot', 'Value']);
  rows.push(['Active rules', autopilot?.activeRules ?? '']);
  rows.push(['Runs', autopilot?.totalRuns30d ?? '']);
  rows.push(['Automated rate (%)', autopilot?.automatedRatePct ?? '']);
  rows.push([]);

  if ((data.byReason ?? []).length > 0) {
    rows.push(['Return reasons', 'Count', '% of returns']);
    for (const reason of data.byReason ?? []) {
      rows.push([reason.label, reason.count ?? '', reason.pct ?? '']);
    }
    rows.push([]);
  }

  rows.push(['Recent activity']);
  rows.push(['Return ID', 'Event', 'Actor', 'Status', 'Date']);
  for (const activity of data.recentActivity ?? []) {
    rows.push([
      activity.returnId,
      humanizeEnum(activity.eventType),
      humanizeEnum(activity.actorType),
      activity.toStatus ? humanizeEnum(activity.toStatus) : '',
      formatTimestamp(activity.createdAt),
    ]);
  }

  return rows;
}

export function exportDashboardCsv(data: DashboardResponse, days: number): void {
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsv(`dashboard-${days}d-${stamp}.csv`, buildDashboardCsvRows(data, days));
}
